<template>
  <div class="card-container" :class="{ 'trade-card': isTradePanel }">
    <div v-if="showChart" class="chart-info">
      <div v-if="!isTradePanel" class="title">
        {{ $t('quote.brief.earnings') }}
      </div>
      <EChart class="chart" :options="chartOptions" :width="280" />
      <div class="desc">
        <label class="mt2">*</label>{{ $t('quote.brief.report-history-desc') }}
      </div>
    </div>
    <div v-else class="financial-info-empty">
      {{ $t('quote.no-data') }}
    </div>
  </div>
</template>
<script>
import EChart from 'com/EChart'
import themeMixin from './theme-mixin'

export default {
  name: 'EarningsCard',
  props: {
    chartData: Object,
    stock: Object,
    isRedUp: Boolean,
    isTradePanel: Boolean
  },
  components: {
    EChart
  },
  mixins: [themeMixin],
  computed: {
    showChart() {
      const hasData = Object.keys(this.chartData).length > 0
      return hasData
    },
    chartPoints() {
      return this.chartData.eps && this.chartData.eps.data
    },
    chartOptions() {
      if (!this.chartData) return {}
      const vm = this
      const options = {
        title: {
          text: `${this.chartData.eps.title}`,
          textStyle: {
            color: this.curTheme[this.theme].fontColor,
            fontSize: 12
          },
          top: 20
        },
        legend: {
          type: 'plain',
          show: true,
          selectedMode: false,
          data: [
            {
              name: this.$t('quote.brief.forecast-tip'),
              textStyle: {
                color: this.curTheme[this.theme].fontColor,
                fontSize: 12,
                fontWeight: 'bold'
              }
            },
            {
              name: this.$t('quote.brief.actual-tip'),
              textStyle: {
                color: this.curTheme[this.theme].fontColor,
                fontSize: 12,
                fontWeight: 'bold'
              }
            }
          ]
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          backgroundColor: 'var(--bg-grey)',
          borderRadius: 0,
          textStyle: {
            fontSize: 12,
            lineHeight: 17,
            color: 'var(--default-font-color)'
          },
          padding: 10,
          position: function(pos, params, el, elRect, size) {
            const obj = { top: 10 }
            obj[['left', 'right'][+(pos[0] < size.viewSize[0] / 2)]] = 30
            return obj
          },
          formatter(params) {
            const dataIndex = params[0].dataIndex
            const changeRate = vm.chartPoints[dataIndex].changeRatio
              ? vm.chartPoints[dataIndex].changeRatio
              : '--'
            const styledChange = `<span style="color: ${vm.getChangeColor(
              changeRate
            )}">${changeRate}</span>`
            const changeTitle = vm.$t('quote.brief.stock-price-change')
            const data1 =
              params[0].value === null || params[0].value === undefined
                ? '--'
                : params[0].value.toFixed(2)
            const data2 =
              params[1].value === null || params[1].value === undefined
                ? '--'
                : params[1].value.toFixed(2)
            return `${params[0].seriesName}: ${data1}<br>${params[1].seriesName}: ${data2}<br>${changeTitle}: ${styledChange}`
          }
        },
        xAxis: {
          axisTick: {
            show: false
          },
          axisLine: {
            show: false
          },
          axisLabel: {
            show: true,
            interval: false,
            align: 'right',
            rotate: 30
          }
        },
        yAxis: {
          show: true,
          axisLine: {
            show: false
          },
          splitLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            textStyle: {
              fontWeight: 'bold',
              color: '#838D9E'
            }
          }
        },
        grid: {
          show: false,
          left: 47
        },
        series: [
          {
            // Expected Eps
            name: this.$t('quote.brief.forecast-tip'),
            type: 'scatter',
            itemStyle: {
              normal: {
                color: '#FF4A4A'
              }
            }
          },
          {
            // Actual Eps
            name: this.$t('quote.brief.actual-tip'),
            type: 'scatter',
            itemStyle: {
              normal: {
                color: '#3CC864'
              }
            }
          }
        ]
      }

      // X Axis Labels
      options.xAxis.data = this.chartPoints.map(item => {
        const valObject = {
          value: item.fiscalQuarterEnding,
          textStyle: {
            fontWeight: 'bold',
            color: '#838D9E'
          }
        }
        return valObject
      })
      // Expected Ep Values
      options.series[0].data = this.chartPoints.map(item => {
        const valObject = {
          value: item.expectedEps
        }
        return valObject
      })
      // Actual Ep Values
      options.series[1].data = this.chartPoints.map(item => {
        const valObject = {
          value: item.actualEps
        }
        return valObject
      })
      return options
    }
  },
  methods: {
    getChangeColor(value) {
      const green = this.curTheme[this.theme].green
      const red = this.curTheme[this.theme].red
      value = parseFloat(value)
      if (value > 0) {
        return this.isRedUp ? red : green
      }
      if (value < 0) {
        return this.isRedUp ? green : red
      }
    }
  }
}
</script>
<style lang="less" scoped>
@import '~uikit';

.card-container {
  .title {
    color: var(--default-font-color);
    font-size: 14px;
    margin-bottom: 8px;
  }
  .chart {
    margin: 0 auto;
  }
  .desc {
    .mt2 {
      position: relative;
      top: 2px;
    }
  }
}
</style>
